/**
 * Symphony GROUP CHAT, Phase 3b — the pure half of the group actions (design §3.1, §3.2, §3.5; contract
 * /tmp/gc3b/contract.md). No React and no fetch: what an answer means, what a refusal says, what the invite dialog
 * discloses, how a mention or a pending post is folded into a transcript. iOS `GroupChatActions.swift` and Android
 * `GroupChatActions.kt` hold the same tables; `group-chat-actions.test.ts` pins this one.
 */
import { contentItemBody, type GroupContent, type UploadState } from './group-content';
import {
  firstName,
  others,
  participantLabel,
  rowGroupSummary,
  parseViewerState,
  resolveAuthor,
  viewerRole as viewerRoleOf,
  type GroupParticipant,
  type GroupSummary,
  type MessageAuthor,
  type GroupMessageMeta,
  type Translate,
} from './group-chat';

export const GROUP_CHAT_ACTIONS = [
  'create-invites', 'invite', 'accept-invite', 'revoke-invite', 'members', 'rename', 'set-role',
  'remove-member', 'leave', 'end', 'viewer-state', 'post-message',
  // Phase 5: members' own Maestros, their owner-private settings, and the public approvals.
  'join-maestro', 'remove-maestro', 'maestro-settings', 'update-maestro-settings', 'resolve-approval',
  // Phase 7: files shared into a group (the live original, by a grant on the file id).
  'share-file', 'update-file-share', 'revoke-file-share', 'file-shares', 'files', 'my-files', 'read-file', 'write-file',
  // Phase 8: stop one Maestro's public work in a group (its owner or a group admin).
  'stop-maestro-work',
  // R5: the caller's own "who can add me to groups".
  'my-settings', 'update-my-settings',
  // Receipts and presence (2026-11-01): typing frames and the online heartbeat.
  'typing', 'presence',
  // Phase 10a: the group's settings and identity, its invite link and join requests, delete for everyone, reports, blocks.
  'update-settings', 'update-info', 'invite-link', 'reset-invite-link', 'preview-link', 'join-by-link', 'join-requests',
  'resolve-join-request', 'delete-message', 'report-group', 'report-message', 'block', 'unblock', 'blocked',
  // Phase 10b: WhatsApp's message actions — react, edit, delete for me, pin, star (and the starred list), forward, info.
  'react', 'edit-message', 'delete-for-me', 'pin', 'star', 'starred', 'forward', 'message-info',
  // Phase 10c: typed messages — a file's upload URL, short-lived file links, a poll vote, an event answer.
  'media-upload', 'media-urls', 'poll-vote', 'event-rsvp',
] as const;
export type GroupChatAction = (typeof GROUP_CHAT_ACTIONS)[number];

// ─── Answers and refusals ────────────────────────────────────────────────────────────────────────────────────

/**
 * One group call's outcome. `refused` is an ordinary answer the UI renders (a 200 carrying `refusal`); `failed` is
 * a transport failure or a malformed answer (show the generic error, keep the state so the reader can retry);
 * `unauthenticated` is a signed-out session (401), which only the invite page acts on.
 */
export type GroupActionOutcome<T = Record<string, unknown>> =
  | { kind: 'ok'; data: T }
  | { kind: 'refused'; refusal: string; data: Record<string, unknown> }
  | { kind: 'failed'; status: number }
  | { kind: 'unauthenticated' };

export function interpretGroupAnswer<T = Record<string, unknown>>(status: number, body: unknown): GroupActionOutcome<T> {
  if (status === 401) return { kind: 'unauthenticated' };
  if (status !== 200 || !body || typeof body !== 'object' || Array.isArray(body)) return { kind: 'failed', status };
  const refusal = (body as { refusal?: unknown }).refusal;
  if (typeof refusal === 'string' && refusal) return { kind: 'refused', refusal: normalizeRefusal(refusal), data: body as Record<string, unknown> };
  return { kind: 'ok', data: body as T };
}

/**
 * A refusal under the one name every rule here reads. symphony-bff has sent `GROUP_GROUP_ENDED` (an enum-prefix typo,
 * fixed server-side) for an ended group; it means GROUP_ENDED. iOS / Android `GroupChatActions` normalize the same.
 */
export function normalizeRefusal(refusal: string): string {
  return refusal === 'GROUP_GROUP_ENDED' ? 'GROUP_ENDED' : refusal;
}

/** Refusal → i18n key. THE SAME TABLE on iOS and Android; anything unknown is the generic error. */
const REFUSAL_KEYS: Record<string, string> = {
  // Phase 7: group files.
  NOT_FILE_OWNER: 'notFileOwner',
  FILE_NOT_SHARED: 'fileNotShared',
  FILE_REVOKED: 'fileRevoked',
  FILE_UNAVAILABLE: 'fileUnavailable',
  FILE_READ_ONLY: 'fileReadOnly',
  FILE_EDIT_CONFLICT: 'fileEditConflict',
  FILE_NOT_EDITABLE: 'fileNotEditable',
  FILE_NOT_SHAREABLE: 'fileNotShareable',
  TRY_AGAIN: 'tryAgain',
  FEATURE_OFF: 'featureOff',
  NOT_A_MEMBER: 'notMember',
  NOT_ADMIN: 'notAdmin',
  NOT_OWNER: 'notOwner',
  GROUP_ENDED: 'groupEnded',
  INVITE_EXPIRED: 'inviteExpired',
  INVITE_REVOKED: 'inviteRevoked',
  INVITE_NOT_FOR_YOU: 'inviteNotForYou',
  INVITE_UNAVAILABLE: 'inviteUnavailable',
  OWNER_MUST_END: 'ownerMustEnd',
  NOT_A_SYMPHONY_ACCOUNT: 'notSymphony',
  CONVERSATION_NOT_FOUND: 'conversationNotFound',
  CANNOT_CHANGE_OWNER: 'cannotChangeOwner',
  TARGET_NOT_A_MEMBER: 'targetNotMember',
  NOT_A_GROUP: 'notAGroup',
  MENTION_NOT_A_MEMBER: 'mentionNotMember',
  REPLY_NOT_AVAILABLE: 'replyNotAvailable',
  MAESTRO_NOT_IN_GROUP: 'maestroNotInGroup',
  NOT_MAESTRO_OWNER: 'notMaestroOwner',
  // Phase 10a.
  SEND_RESTRICTED: 'sendRestricted',
  NOT_ALLOWED_BY_SETTINGS: 'notAllowedBySettings',
  INVITE_LINK_INVALID: 'inviteLinkInvalid',
  JOIN_RATE_LIMITED: 'joinRateLimited',
  JOIN_REQUEST_UNAVAILABLE: 'joinRequestUnavailable',
  MESSAGE_NOT_AVAILABLE: 'messageNotAvailable',
  NOT_MESSAGE_AUTHOR: 'notMessageAuthor',
  CANNOT_BLOCK_SELF: 'cannotBlockSelf',
  STALE_SETTINGS: 'staleSettings',
  // Phase 10b (NOT_MESSAGE_AUTHOR reads per action — lib/group-message-actions.ts `messageActionErrorKey`).
  EDIT_WINDOW_EXPIRED: 'editWindowExpired',
  DELETE_WINDOW_EXPIRED: 'deleteWindowExpired',
  PIN_LIMIT_REACHED: 'pinLimitReached',
  MESSAGE_DELETED: 'messageDeleted',
  MESSAGE_NOT_EDITABLE: 'messageNotEditable',
  SAVED_LIMIT_REACHED: 'savedLimitReached',
  // Phase 10c: typed messages.
  MEDIA_NOT_ALLOWED: 'mediaNotAllowed',
  MEDIA_NOT_FOUND: 'mediaNotFound',
  WRONG_MESSAGE_TYPE: 'wrongMessageType',
  INVALID_VOTE: 'invalidVote',
};
export function refusalKey(refusal: string | null | undefined): string {
  return `chat.group.error.${(refusal && REFUSAL_KEYS[refusal]) || 'generic'}`;
}
/** The message for any non-ok outcome. */
export function outcomeErrorKey(outcome: GroupActionOutcome<unknown>): string {
  return outcome.kind === 'refused' ? refusalKey(outcome.refusal) : refusalKey(null);
}

/**
 * The refusals that mean "you are no longer in this group": the client drops the row, clears the cached
 * transcript and leaves the chat with a notice (contract item 8). Null for every other refusal.
 */
export function lostAccessNotice(refusal: string | null | undefined): 'chat.group.removedNotice' | 'chat.group.endedNotice' | null {
  if (refusal === 'NOT_A_MEMBER') return 'chat.group.removedNotice';
  if (refusal && normalizeRefusal(refusal) === 'GROUP_ENDED') return 'chat.group.endedNotice';
  return null;
}

/**
 * What the chat shows in place of the transcript's composer once the server refuses the viewer with one of the
 * lost-access codes (THE SAME RULE as iOS `lostGroupState` / Android `lostGroupState`): GROUP_ENDED → the group has
 * ended; NOT_A_MEMBER → "You were removed" for someone this client knew as a member, else "You're not in this group"
 * (a link to a group they never joined). Null for every other refusal.
 */
export type LostGroupState = 'notMember' | 'removed' | 'ended';
export function lostGroupState(refusal: string | null | undefined, wasMember: boolean): LostGroupState | null {
  if (refusal && normalizeRefusal(refusal) === 'GROUP_ENDED') return 'ended';
  if (refusal === 'NOT_A_MEMBER') return wasMember ? 'removed' : 'notMember';
  return null;
}

/** The i18n keys of a lost-group state: its title and its one line under it. */
export function lostGroupKeys(lost: LostGroupState): { title: string; body: string } {
  return { title: `chat.group.lost.${lost}.title`, body: `chat.group.lost.${lost}.body` };
}

/**
 * Phase 4: the harness refuses a direct turn into a live group with the typed code `GROUP_CHAT_POST_ONLY` (the HTTP
 * error body of a stream or steer, `{errorCode}` once orion-web's route has typed it, or the raw Wix error). The chat
 * is a group from then on, and the text goes through `post-message` instead.
 */
export function isGroupPostOnlyError(body: unknown): boolean {
  let parsed: unknown = body;
  if (typeof body === 'string') {
    try {
      parsed = JSON.parse(body);
    } catch {
      return false;
    }
  }
  if (!parsed || typeof parsed !== 'object') return false;
  const b = parsed as { errorCode?: unknown; code?: unknown; details?: { applicationError?: { code?: unknown } } };
  return b.errorCode === 'GROUP_CHAT_POST_ONLY' || b.code === 'GROUP_CHAT_POST_ONLY' || b.details?.applicationError?.code === 'GROUP_CHAT_POST_ONLY';
}

// ─── Add people ──────────────────────────────────────────────────────────────────────────────────────────────

export type HistoryPolicy = 'ALL' | 'FROM_DATE' | 'AFTER_JOIN';

/** An invitee as the dialog holds them: the address the invite goes to, and the name to call them by. */
export interface Invitee {
  email: string;
  name?: string;
}

/** "Dana" for a named invitee, the address's local part ("dana.levi") for a typed address. */
export function inviteeFirstName(invitee: Invitee): string {
  const named = invitee.name?.trim();
  if (named && named !== invitee.email) return firstName(named);
  return invitee.email.split('@')[0] ?? invitee.email;
}

/** `{names}` of the disclosure line: the invitees' names (or addresses), joined by ", ". */
export function inviteeNames(invitees: Invitee[]): string {
  return invitees.map((i) => (i.name?.trim() && i.name.trim() !== i.email ? i.name.trim() : i.email)).join(', ');
}

/** The D10 disclosure line for one batch. `date` is the localized FROM_DATE day (ignored otherwise). */
export function disclosureLine(policy: HistoryPolicy, invitees: Invitee[], date: string, t: Translate): string {
  const names = inviteeNames(invitees);
  if (policy === 'ALL') return t('chat.group.disclosureAll', { names });
  if (policy === 'FROM_DATE') return t('chat.group.disclosureFromDate', { names, date });
  return t('chat.group.disclosureAfterJoin', { names });
}

/**
 * The first-share group name: the other people's first names — the chat's existing human participants (never its
 * private title, which can leak content, §7.1) and the invitees — three, then ", +N". Empty when nobody is named.
 */
export function suggestGroupName(invitees: Invitee[], summary?: GroupSummary | null): string {
  const existing = summary ? others(summary).filter((p) => p.kind === 'HUMAN').map((p) => firstName(p.displayName)) : [];
  const names = [...existing, ...invitees.map(inviteeFirstName)].map((n) => n.trim()).filter(Boolean);
  const unique = names.filter((n, i) => names.indexOf(n) === i);
  if (unique.length <= 3) return unique.join(', ');
  return `${unique.slice(0, 3).join(', ')}, +${unique.length - 3}`;
}

/**
 * A NEW group must be named (first share / Add people on a private chat): nothing is prefilled — the suggested first
 * names are only the field's placeholder — and Send stays off until a non-blank name is typed. Adding people to an
 * existing group asks no name (an admin renames it from the group's sheet). Same gate on iOS and Android.
 */
export function groupNameSatisfied(firstShare: boolean, typedName: string): boolean {
  return !firstShare || typedName.trim().length > 0;
}

/** The YYYY-MM-DD a date input holds → the ISO instant (local midnight) `historyFrom` carries. */
export function historyFromIso(day: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}

/** The `create-invites` body for one batch. FROM_DATE without a valid day is not sendable (null). */
export function createInvitesBody(input: {
  conversationId: string;
  invitees: Invitee[];
  policy: HistoryPolicy;
  fromDay?: string;
  groupName?: string;
  confirmUnknown?: boolean;
  /**
   * The Maestro's group-setup card this create came from (`lib/group-setup-card.ts`): symphony-bff writes `created` onto
   * that row, so the card turns into the group's status card. `CreateGroupInvitesRequest.setup_card` (new, optional).
   */
  setupCard?: { conversationId: string; messageId: string } | null;
}): Record<string, unknown> | null {
  if (input.invitees.length === 0 || input.invitees.length > 10) return null;
  const historyFrom = input.policy === 'FROM_DATE' ? historyFromIso(input.fromDay ?? '') : null;
  if (input.policy === 'FROM_DATE' && !historyFrom) return null;
  const name = input.groupName?.trim();
  return {
    conversationId: input.conversationId,
    emails: input.invitees.map((i) => i.email),
    historyPolicy: input.policy,
    ...(historyFrom ? { historyFrom } : {}),
    ...(name ? { groupName: name } : {}),
    ...(input.confirmUnknown ? { confirmUnknown: true } : {}),
    ...(input.setupCard ? { setupCard: { conversationId: input.setupCard.conversationId, messageId: input.setupCard.messageId } } : {}),
  };
}

export interface InviteWire {
  inviteId: string;
  conversationId: string;
  email: string;
  name: string;
  historyPolicy: HistoryPolicy;
  historyFrom: string | null;
  state: 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';
  expiresDate: string | null;
  inviteLink: string;
}
const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
export function parseInvite(raw: unknown): InviteWire | null {
  if (!isObj(raw) || typeof raw.inviteId !== 'string' || !raw.inviteId) return null;
  const policy = raw.historyPolicy === 'FROM_DATE' || raw.historyPolicy === 'AFTER_JOIN' ? raw.historyPolicy : 'ALL';
  const state = raw.state === 'ACCEPTED' || raw.state === 'REVOKED' || raw.state === 'EXPIRED' ? raw.state : 'PENDING';
  const s = (v: unknown) => (typeof v === 'string' ? v : '');
  return {
    inviteId: raw.inviteId,
    conversationId: s(raw.conversationId),
    email: s(raw.email),
    name: s(raw.name),
    historyPolicy: policy,
    historyFrom: s(raw.historyFrom) || null,
    state,
    expiresDate: s(raw.expiresDate) || null,
    inviteLink: s(raw.inviteLink),
  };
}

/** R5: why a person was invited rather than added directly. */
export type InviteReason = 'NOT_ON_SYMPHONY' | 'PRIVACY';
const inviteReasonOf = (raw: unknown): InviteReason | null => (raw === 'NOT_ON_SYMPHONY' || raw === 'PRIVACY' ? raw : null);

/** How a notified person was told: APP (a push to the Symphony app they have) or EMAIL. Null when the server does not say. */
export type NotifiedVia = 'APP' | 'EMAIL';
const notifiedViaOf = (raw: unknown): NotifiedVia | null => (raw === 'APP' || raw === 'EMAIL' ? raw : null);

/**
 * What the result list says for one recipient of a batch. R5: an existing Symphony user is ADDED (in the group now, no
 * accept step); anyone else is invited, with the reason — not on Symphony yet, or their own privacy setting.
 */
export type InviteResult =
  /** `notified` false: nothing reached them; `via` null with `notified`: an older server that does not say how. */
  | { email: string; kind: 'added'; name: string; notified: boolean; via: NotifiedVia | null }
  | { email: string; kind: 'notified'; reason: InviteReason | null; via: NotifiedVia | null }
  | { email: string; kind: 'shareLink'; link: string; alreadyInvited: boolean; reason: InviteReason | null }
  | { email: string; kind: 'alreadyMember' }
  | { email: string; kind: 'self' }
  /** Phase 10a: that person blocked the caller — the row says no more than "couldn't add". */
  | { email: string; kind: 'notAdded' }
  | { email: string; kind: 'failed' };

export function inviteResults(outcomes: unknown): InviteResult[] {
  if (!Array.isArray(outcomes)) return [];
  return outcomes.filter(isObj).map((o): InviteResult => {
    const email = typeof o.email === 'string' ? o.email : '';
    const invite = parseInvite(o.invite);
    const reason = inviteReasonOf(o.inviteReason);
    if (o.result === 'ADDED') {
      const name = isObj(o.member) && typeof o.member.name === 'string' ? o.member.name.trim() : '';
      return { email, kind: 'added', name, notified: o.notified !== false, via: o.notified === true ? notifiedViaOf(o.notifiedVia) : null };
    }
    if (o.result === 'ALREADY_MEMBER') return { email, kind: 'alreadyMember' };
    if (o.result === 'SELF') return { email, kind: 'self' };
    if (o.result === 'NOT_ADDED') return { email, kind: 'notAdded' };
    if (o.result === 'INVITED' && o.notified === true) return { email, kind: 'notified', reason, via: notifiedViaOf(o.notifiedVia) };
    if ((o.result === 'INVITED' || o.result === 'ALREADY_INVITED') && invite?.inviteLink) {
      return { email, kind: 'shareLink', link: invite.inviteLink, alreadyInvited: o.result === 'ALREADY_INVITED', reason };
    }
    if (o.result === 'INVITED' || o.result === 'ALREADY_INVITED') return { email, kind: 'notified', reason, via: null };
    return { email, kind: 'failed' };
  });
}

const REASON_KEYS: Record<InviteReason, string> = {
  NOT_ON_SYMPHONY: 'chat.group.result.invitedNotOnSymphony',
  PRIVACY: 'chat.group.result.invitedPrivacy',
};

/** The row's title: an added person by their name, everyone else by the address. */
export function inviteResultTitle(r: InviteResult): string {
  return r.kind === 'added' && r.name ? r.name : r.email;
}

/** The row's lines, as i18n keys in order (iOS / Android `inviteResultLines`). The link controls go under them. */
export function inviteResultLines(r: InviteResult): string[] {
  switch (r.kind) {
    // 2026-11-02: the line names the one channel that told them, never "a notification or an email" (a person with no app was
    // "notified" by a push that reached nobody). An older server that does not say gets the plain line, as before.
    case 'added':
      if (!r.notified) return ['chat.group.result.added', 'chat.group.result.addedNotNotified'];
      if (r.via === 'EMAIL') return ['chat.group.result.added', 'chat.group.result.addedEmailed'];
      if (r.via === 'APP') return ['chat.group.result.added', 'chat.group.result.addedPushed'];
      return ['chat.group.result.added'];
    case 'notified': {
      const first = r.reason ? REASON_KEYS[r.reason] : 'chat.group.result.notified';
      if (r.via === 'EMAIL') return [first, 'chat.group.result.invitedEmailed'];
      if (r.via === 'APP') return [first, 'chat.group.result.invitedPushed'];
      return [first];
    }
    case 'shareLink':
      if (r.alreadyInvited) return ['chat.group.result.alreadyInvited', 'chat.group.result.shareLink'];
      return r.reason ? [REASON_KEYS[r.reason], 'chat.group.result.shareLink'] : ['chat.group.result.shareLink'];
    case 'alreadyMember':
      return ['chat.group.result.alreadyMember'];
    case 'self':
      return ['chat.group.result.self'];
    case 'notAdded':
      return ['chat.group.result.notAdded'];
    default:
      return ['chat.group.error.generic'];
  }
}

/** True when the batch put someone in the group now: the participant list is re-read at once. */
export function anyAdded(results: InviteResult[]): boolean {
  return results.some((r) => r.kind === 'added');
}

// ─── R5: "Who can add me to groups" ─────────────────────────────────────────────────────────────────────────

export type WhoCanAdd = 'EVERYONE' | 'MY_CONTACTS' | 'NOBODY';
export const WHO_CAN_ADD_OPTIONS: readonly { value: WhoCanAdd; labelKey: string; hintKey: string }[] = [
  { value: 'EVERYONE', labelKey: 'settings.groupAdd.everyone', hintKey: 'settings.groupAdd.everyoneHint' },
  { value: 'MY_CONTACTS', labelKey: 'settings.groupAdd.myContacts', hintKey: 'settings.groupAdd.myContactsHint' },
  { value: 'NOBODY', labelKey: 'settings.groupAdd.nobody', hintKey: 'settings.groupAdd.nobodyHint' },
];
/** `settings.whoCanAddMe` of a `my-settings` / `update-my-settings` answer; unset or unknown reads EVERYONE (the default). */
export function parseWhoCanAdd(data: unknown): WhoCanAdd {
  const raw = isObj(data) && isObj(data.settings) ? data.settings.whoCanAddMe : undefined;
  return raw === 'MY_CONTACTS' || raw === 'NOBODY' ? raw : 'EVERYONE';
}
export function whoCanAddLabelKey(value: WhoCanAdd): string {
  return WHO_CAN_ADD_OPTIONS.find((o) => o.value === value)!.labelKey;
}

// ─── The invite page ─────────────────────────────────────────────────────────────────────────────────────────

/** The invite screen's state, from the `invite` read (and, after Join, `accept-invite`). */
export type InviteScreen =
  | { kind: 'preview'; inviterName: string; groupName: string; policy: HistoryPolicy; historyFrom: string | null; conversationId: string }
  | { kind: 'notForYou' }
  | { kind: 'expired' }
  | { kind: 'revoked' }
  | { kind: 'unavailable' }
  | { kind: 'ended' }
  | { kind: 'needsAccount' }
  | { kind: 'error'; key: string };

export function inviteScreenFor(outcome: GroupActionOutcome): InviteScreen {
  if (outcome.kind === 'refused') return inviteRefusalScreen(outcome.refusal);
  if (outcome.kind !== 'ok') return { kind: 'error', key: refusalKey(null) };
  const invite = parseInvite(outcome.data.invite);
  const group = isObj(outcome.data.group) ? outcome.data.group : null;
  if (outcome.data.addressedToYou === false) return { kind: 'notForYou' };
  if (!invite) return { kind: 'unavailable' };
  if (invite.state === 'EXPIRED') return { kind: 'expired' };
  if (invite.state === 'REVOKED') return { kind: 'revoked' };
  if (group && group.active === false) return { kind: 'ended' };
  return {
    kind: 'preview',
    inviterName: typeof outcome.data.inviterName === 'string' ? outcome.data.inviterName : '',
    groupName: (group && typeof group.name === 'string' ? group.name : '') || invite.name,
    policy: invite.historyPolicy,
    historyFrom: invite.historyFrom,
    conversationId: invite.conversationId || (group && typeof group.conversationId === 'string' ? group.conversationId : ''),
  };
}

export function inviteRefusalScreen(refusal: string): InviteScreen {
  switch (refusal) {
    case 'INVITE_NOT_FOR_YOU': return { kind: 'notForYou' };
    case 'INVITE_EXPIRED': return { kind: 'expired' };
    case 'INVITE_REVOKED': return { kind: 'revoked' };
    case 'INVITE_UNAVAILABLE': return { kind: 'unavailable' };
    case 'GROUP_ENDED': return { kind: 'ended' };
    // A signed-in Wix user with no Symphony yet: through onboarding, then back here (T36).
    case 'NOT_A_SYMPHONY_ACCOUNT': return { kind: 'needsAccount' };
    default: return { kind: 'error', key: refusalKey(refusal) };
  }
}

export const INVITE_SCREEN_KEYS: Partial<Record<InviteScreen['kind'], string>> = {
  notForYou: 'chat.group.invite.notForYou',
  expired: 'chat.group.invite.expired',
  revoked: 'chat.group.invite.revoked',
  unavailable: 'chat.group.invite.unavailable',
  ended: 'chat.group.invite.ended',
};

export function inviteHistoryLine(policy: HistoryPolicy, date: string, t: Translate): string {
  if (policy === 'ALL') return t('chat.group.invite.historyAll');
  if (policy === 'FROM_DATE') return t('chat.group.invite.historyFromDate', { date });
  return t('chat.group.invite.historyAfterJoin');
}

/** The invite id the deep link carries: a GUID, or a dev fixture id. Anything else is not an invite. */
export function parseInviteId(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const id = raw.trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) || /^mock-invite-\d+$/.test(id) ? id : null;
}

// ─── Mentions ────────────────────────────────────────────────────────────────────────────────────────────────

export interface GroupMentionCandidate {
  id: string;
  /** The token text: a human's first name, a Maestro's own name. */
  name: string;
  /** The row label (a Maestro reads "{agentName} · {owner}'s assistant"). */
  label: string;
  participant: GroupParticipant;
}

/**
 * The `@` panel's two sections in a group: People (other humans) and Assistants (Maestros), each filtered by the
 * typed query (case-insensitive, on the label). The viewer is never offered.
 */
export function groupMentionSections(summary: GroupSummary, query: string, t: Translate): { people: GroupMentionCandidate[]; assistants: GroupMentionCandidate[] } {
  const q = query.trim().toLowerCase();
  const candidates = others(summary).map((p) => ({
    id: p.id,
    // The WHOLE display name — "Caro's Maestro", "The Maestro", "Dana Levi" — never cut at a space or an apostrophe;
    // a nameless person's role fallback ("Group owner"). iOS / Android `mentionName` insert the same.
    name: p.displayName.trim(),
    label: participantLabel(p, t),
    participant: p,
  })).filter((c) => c.name && (!q || c.label.toLowerCase().includes(q)));
  return {
    people: candidates.filter((c) => c.participant.kind === 'HUMAN'),
    assistants: candidates.filter((c) => c.participant.kind === 'MAESTRO'),
  };
}

/** A pick in the @ panel: a person by their id, a Maestro by its OWNER's id (Phase 4 — that is how it is asked). */
export interface PickedMention {
  id: string;
  name: string;
  kind: 'HUMAN' | 'MAESTRO';
  ownerId?: string;
}
export function pickedMention(c: GroupMentionCandidate): PickedMention {
  return { id: c.id, name: c.name, kind: c.participant.kind, ...(c.participant.ownerId ? { ownerId: c.participant.ownerId } : {}) };
}

/**
 * The post's two mention lists: people → `mentionedMetaSiteIds`; a Maestro → its owner in
 * `mentionedMaestroOwnerMetaSiteIds`. A pick whose `@Name` was deleted before sending is dropped, as is a Maestro
 * with no known owner.
 */
export function mentionsForSend(picked: PickedMention[], text: string): { mentionedMetaSiteIds: string[]; mentionedMaestroOwnerMetaSiteIds: string[] } {
  const kept = picked.filter((p) => text.includes(`@${p.name.trim()}`));
  const uniq = (ids: string[]) => ids.filter((id, i) => ids.indexOf(id) === i);
  return {
    mentionedMetaSiteIds: uniq(kept.filter((p) => p.kind === 'HUMAN').map((p) => p.id)),
    mentionedMaestroOwnerMetaSiteIds: uniq(kept.filter((p) => p.kind === 'MAESTRO' && p.ownerId).map((p) => p.ownerId as string)),
  };
}

/** The ids to send: every picked mention whose `@Name` is still in the text (a deleted token drops its id). */
export function mentionedIdsForSend(picked: { id: string; name: string }[], text: string): string[] {
  const ids: string[] = [];
  for (const p of picked) {
    if (text.includes(`@${p.name.trim()}`) && !ids.includes(p.id)) ids.push(p.id);
  }
  return ids;
}

// ─── Posting ─────────────────────────────────────────────────────────────────────────────────────────────────

/** A post as the composer holds it until the server's copy is in the transcript. */
export interface PendingGroupPost {
  /** Minted once per message, REUSED on retry; the server makes it the message id. */
  clientMessageId: string;
  conversationId: string;
  text: string;
  mentionedMetaSiteIds: string[];
  /** Phase 4: a Maestro asked by name, as its OWNER's metasite. */
  mentionedMaestroOwnerMetaSiteIds?: string[];
  replyToMessageId?: string;
  /** Phase 10c: a typed message (its `text` is then the caption, or empty). */
  content?: GroupContent;
  /** Phase 10c: a file still on its way (the bubble's progress / cancel / retry), and its local preview. */
  upload?: { state: UploadState; localUrl?: string };
  createdDate: string;
  state: 'sending' | 'failed';
  /** The mapped refusal (or the generic error) of the last failed attempt. */
  errorKey?: string;
}

export function postMessageBody(post: PendingGroupPost): Record<string, unknown> {
  return {
    conversationId: post.conversationId,
    text: post.text,
    clientMessageId: post.clientMessageId,
    mentionedMetaSiteIds: post.mentionedMetaSiteIds,
    ...(post.mentionedMaestroOwnerMetaSiteIds?.length ? { mentionedMaestroOwnerMetaSiteIds: post.mentionedMaestroOwnerMetaSiteIds } : {}),
    ...(post.replyToMessageId ? { replyToMessageId: post.replyToMessageId } : {}),
    ...(post.content ? { contentItem: contentItemBody(post.content) } : {}),
  };
}

// ─── Phase 4: "{agentName} is working" after asking a Maestro ────────────────────────────────────────────────

export const MAESTRO_WORKING_MS = 3 * 60_000;

/** A Maestro asked in a group: which conversation, whose Maestro, when, and the replies already there at the time. */
export interface MaestroWorking {
  conversationId: string;
  ownerId: string;
  since: number;
  /** ASSISTANT / MAESTRO rows present when the question went out — a reply is any row NOT in here. */
  knownReplyIds: string[];
  /** Phase 8: the reader tapped Stop (in flight), or the service stopped it. Absent = still working. */
  stop?: 'stopping' | 'stopped';
}

type TranscriptRow = {
  id?: string;
  finalId?: string;
  role?: string;
  author?: MessageAuthor | null;
  conversationId?: string;
  groupMeta?: Pick<GroupMessageMeta, 'workStopped'> | null;
};
const isMaestroReply = (m: TranscriptRow) => m.role === 'ASSISTANT' || m.author?.kind === 'MAESTRO';

/**
 * Whether a post asks a Maestro, and which one: the first Maestro it mentions, else the Maestro whose row it replies
 * to. Null for a post that asks none (no indicator — a plain post wakes no assistant).
 */
export function maestroAskedBy(
  post: { conversationId: string; mentionedMaestroOwnerMetaSiteIds?: string[]; replyToMessageId?: string },
  summary: GroupSummary,
  transcript: TranscriptRow[],
  now: number,
): MaestroWorking | null {
  let ownerId = post.mentionedMaestroOwnerMetaSiteIds?.[0];
  if (!ownerId && post.replyToMessageId) {
    const target = transcript.find((m) => m.id === post.replyToMessageId || m.finalId === post.replyToMessageId);
    if (target?.author?.kind === 'MAESTRO') ownerId = target.author.agentOwnerMetaSiteId ?? resolveAuthor(summary, target.author)?.ownerId;
  }
  if (!ownerId) return null;
  const knownReplyIds = transcript.filter(isMaestroReply).map((m) => m.finalId ?? m.id).filter((id): id is string => !!id);
  return { conversationId: post.conversationId, ownerId, since: now, knownReplyIds };
}

/**
 * Whether the indicator still shows: the chat it was started in, under 3 minutes old, and no new ASSISTANT / MAESTRO
 * row has arrived in that chat since. Leaving the chat clears it (the caller drops the state).
 */
export function maestroWorkingVisible(working: MaestroWorking | null, conversationId: string | null, transcript: TranscriptRow[], now: number): boolean {
  if (!working || working.conversationId !== conversationId) return false;
  if (now - working.since >= MAESTRO_WORKING_MS) return false;
  const known = new Set(working.knownReplyIds);
  return !transcript.some((m) => isMaestroReply(m) && !known.has(m.finalId ?? m.id ?? '') && (!m.conversationId || m.conversationId === conversationId));
}

/**
 * Phase 8: what the line under the transcript draws. Working and stopping follow {@link maestroWorkingVisible}. Stopped
 * stays (past the 3 minutes and past new replies) until the SYSTEM "was stopped" row for THAT Maestro arrives — the
 * transcript then carries the stopped state — or the next ask replaces the whole state.
 */
export type MaestroWorkingLine = 'hidden' | 'working' | 'stopping' | 'stopped';
export function maestroWorkingLine(working: MaestroWorking | null, conversationId: string | null, transcript: TranscriptRow[], now: number): MaestroWorkingLine {
  if (!working || working.conversationId !== conversationId) return 'hidden';
  if (working.stop === 'stopped') {
    const known = new Set(working.knownReplyIds);
    const confirmed = transcript.some(
      (m) => m.groupMeta?.workStopped?.maestroOwnerMetaSiteId === working.ownerId && !known.has(m.finalId ?? m.id ?? '') && (!m.conversationId || m.conversationId === conversationId),
    );
    return confirmed ? 'hidden' : 'stopped';
  }
  if (!maestroWorkingVisible(working, conversationId, transcript, now)) return 'hidden';
  return working.stop === 'stopping' ? 'stopping' : 'working';
}

/** The Maestro's name for the indicator: its participant's (already named) display name. */
export function maestroWorkingName(working: MaestroWorking, summary: GroupSummary, fallbackName: string): string {
  return summary.participants.find((p) => p.kind === 'MAESTRO' && p.ownerId === working.ownerId)?.displayName || fallbackName;
}

/** The transcript row a pending post draws: the viewer's own bubble, keyed by its clientMessageId. */
export interface GroupPostRow {
  id: string;
  conversationId: string;
  role: 'USER';
  content: string;
  createdDate: string;
  author?: MessageAuthor;
  groupMeta?: GroupMessageMeta;
  groupPost: { state: 'sending' | 'failed'; errorKey?: string; upload?: { state: UploadState; localUrl?: string } };
}
export function pendingPostRow(post: PendingGroupPost, viewerId: string | undefined): GroupPostRow {
  return {
    id: post.clientMessageId,
    conversationId: post.conversationId,
    role: 'USER',
    content: post.text,
    createdDate: post.createdDate,
    ...(viewerId ? { author: { principalMetaSiteId: viewerId, kind: 'HUMAN' as const } } : {}),
    ...(post.replyToMessageId || post.content
      ? { groupMeta: { ...(post.replyToMessageId ? { replyToMessageId: post.replyToMessageId } : {}), ...(post.content ? { content: post.content } : {}) } }
      : {}),
    groupPost: {
      state: post.state,
      ...(post.errorKey ? { errorKey: post.errorKey } : {}),
      ...(post.upload ? { upload: post.upload } : {}),
    },
  };
}

/**
 * The transcript with this conversation's live group rows folded in: server rows (loaded, or arrived over the
 * realtime channel) appended when the loaded transcript does not hold them yet, then the pending posts that are
 * still not in it. Dedupe is by id — a post's clientMessageId IS its message id.
 *
 * A pending post (sending or failed) takes its place by its OWN creation time: it goes after every row that is not
 * newer than it, so a failed photo stays where it was sent instead of sinking below the messages that came after it.
 * Pending posts live in their own store, so a re-read of the window never drops one; only a discard (or the server's
 * copy arriving) does.
 */
export function mergeGroupRows<M extends { id?: string; finalId?: string; createdDate?: string }>(messages: M[], live: M[], pending: M[]): M[] {
  const seen = new Set<string>();
  for (const m of messages) {
    if (m.id) seen.add(m.id);
    if (m.finalId) seen.add(m.finalId);
  }
  const out = [...messages];
  for (const m of live) {
    if (!m.id || seen.has(m.id)) continue;
    seen.add(m.id);
    out.push(m);
  }
  const time = (m: M) => (m.createdDate ? Date.parse(m.createdDate) : NaN);
  for (const m of pending) {
    if (!m.id || seen.has(m.id)) continue;
    seen.add(m.id);
    const at = time(m);
    let i = out.length;
    // Walk back over rows strictly newer than this post; a row with no date stops the walk (its place is unknown).
    if (!Number.isNaN(at)) while (i > 0 && time(out[i - 1]) > at) i--;
    out.splice(i, 0, m);
  }
  return out;
}

/** The id of the line `applyGroupEnded` draws locally the moment the owner ends a group. */
export const LOCAL_ENDED_LINE_PREFIX = 'group-ended-';

/**
 * A7 (2026-11-01 hands-on): "You ended the group" drew TWICE — the local line `applyGroupEnded` appends and the server's
 * GROUP_ENDED row (a realtime `created` / `ended` frame, or the next window read) have different ids. The server's row
 * wins: every local ended line is dropped once any other ENDED row is held. The same array when nothing is dropped.
 * iOS / Android `oneEndedLine` decide the same.
 */
export function oneEndedLine<M extends { id?: string; groupEvent?: { type?: string } | null; groupMeta?: { infoEvent?: { kind?: string } | null } | null }>(rows: M[]): M[] {
  const ends = (m: M) => m.groupEvent?.type === 'ENDED' || m.groupMeta?.infoEvent?.kind === 'GROUP_ENDED';
  const local = (m: M) => !!m.id?.startsWith(LOCAL_ENDED_LINE_PREFIX);
  if (!rows.some((m) => ends(m) && !local(m)) || !rows.some(local)) return rows;
  return rows.filter((m) => !local(m));
}

/**
 * Phase 10a: rows replaced in place — a message deleted for everyone becomes the tombstone the service answered (or
 * the realtime channel sent), whichever copy of the transcript holds it. The same array when nothing is replaced.
 */
export function applyGroupReplacements<M extends { id?: string; finalId?: string }>(rows: M[], replaced: Record<string, M> | undefined): M[] {
  if (!replaced || Object.keys(replaced).length === 0) return rows;
  let changed = false;
  const out = rows.map((m) => {
    const r = (m.id && replaced[m.id]) || (m.finalId && replaced[m.finalId]);
    if (!r) return m;
    changed = true;
    return r;
  });
  return changed ? out : rows;
}

/**
 * Who a reply quotes, by the quoted row's `author` (MessageAuthor) resolved against the group: "You" ONLY when that
 * author is the viewer's own HUMAN participant, else the participant's label. Never from `role` — in a group every
 * person's row is a USER row — so an author-less row or an unknown author names nobody (undefined), never "You".
 */
export function replyQuoteSender(summary: GroupSummary, author: MessageAuthor | null | undefined, t: Translate): string | undefined {
  if (!author) return undefined;
  if (author.kind === 'HUMAN' && !!summary.viewerId && author.principalMetaSiteId === summary.viewerId) return t('chat.reply.you');
  const who = resolveAuthor(summary, author);
  return who ? participantLabel(who, t) || undefined : undefined;
}

/**
 * A group reply's quote, resolved from the LOADED transcript by `replyToMessageId` (never fetched — a target the
 * reader cannot see shows nothing). The sender is `replyQuoteSender` of the target's author.
 */
export function replyQuoteFor(
  summary: GroupSummary,
  messages: { id?: string; finalId?: string; content?: string; role?: string; author?: MessageAuthor | null }[],
  replyToMessageId: string | undefined,
  t: Translate,
): { messageId: string; preview?: string; sender?: string } | null {
  if (!replyToMessageId) return null;
  const target = messages.find((m) => m.id === replyToMessageId || m.finalId === replyToMessageId);
  if (!target) return null;
  const preview = (target.content ?? '').replace(/\s+/g, ' ').trim().slice(0, 140);
  const sender = replyQuoteSender(summary, target.author, t);
  return { messageId: replyToMessageId, ...(preview ? { preview } : {}), ...(sender ? { sender } : {}) };
}

/**
 * What the composer does with a send. A group posts through `post-message` (the owner too — a post wakes no
 * assistant); everything else is today's assistant path, untouched. `enabled` is `symphonyGroupChat`.
 */
export function composerRoute(enabled: boolean, summary: GroupSummary | null | undefined, conversationId: string | null): 'group-post' | 'assistant' {
  return enabled && summary && conversationId ? 'group-post' : 'assistant';
}

/**
 * Whether the composer offers attachments (the "+" menu, file drop, file paste). A group post is text only, so a
 * group offers none — a file staged there could only be dropped at send. Every private chat, and the flag off, keep
 * them exactly as today.
 */
export function composerAttachmentsOffered(enabled: boolean, summary: GroupSummary | null | undefined, conversationId: string | null): boolean {
  return composerRoute(enabled, summary, conversationId) !== 'group-post';
}

/** Who may do what in the participant sheet (design §3.5, contract item 7). */
export function memberActions(viewer: 'OWNER' | 'ADMIN' | 'MEMBER' | null, target: { role: 'OWNER' | 'ADMIN' | 'MEMBER'; you: boolean }) {
  const isOwner = viewer === 'OWNER';
  const isAdmin = viewer === 'ADMIN';
  if (target.you || target.role === 'OWNER') return { makeAdmin: false, removeAdmin: false, remove: false };
  return {
    makeAdmin: isOwner && target.role === 'MEMBER',
    removeAdmin: isOwner && target.role === 'ADMIN',
    remove: isOwner || (isAdmin && target.role === 'MEMBER'),
  };
}

/**
 * The chat list without the JOINED groups the viewer archived — web has no archive screen, so an archived group is
 * hidden from the list and stays reachable through search (a non-empty query shows it). Flag off: unchanged.
 */
export function withoutArchivedGroups<R extends { viewerState?: unknown }>(rows: R[], enabled: boolean, searching: boolean): R[] {
  if (!enabled || searching) return rows;
  const kept = rows.filter((r) => !parseViewerState(r.viewerState)?.archived);
  return kept.length === rows.length ? rows : kept;
}

/** The "Groups" chat-list filter: the rows that are Symphony group chats (a parseable `group` summary — or an ended one). */
export function onlyGroups<R extends { group?: unknown; endedGroup?: unknown }>(rows: R[]): R[] {
  const kept = rows.filter((r) => rowGroupSummary(r) !== null);
  return kept.length === rows.length ? rows : kept;
}

/**
 * Whether the chat menu offers "Add people": the owner on a private chat of their own (the first invite makes it a
 * group), and the owner and admins on a group. Never with the flag off, on a brand-new chat, or on a JOINED row
 * that is not a group in this client (a member's view without a summary).
 */
export function addPeopleOffered(enabled: boolean, conversationId: string | null | undefined, summary: GroupSummary | null, joined: boolean): boolean {
  if (!enabled || !conversationId) return false;
  if (!summary) return !joined;
  const role = viewerRoleOf(summary);
  return role === 'OWNER' || role === 'ADMIN';
}
